import React, {useRef, useState} from "react";
import GetDateFilter from "./GetDateFilter";
import getTimezoneOffset from "../../../../../utils/GetTimeZone";
import {SimpleSearch} from "../../../../../reduxToolkit/Search/SearchApi";
import {useDispatch} from "react-redux";
import {useTranslation} from "react-i18next";
import CustomTh from "../../../../../components/table/CustomTh";
import CustomTd from "../../../../../components/table/CustomTd";
import TableFunction from "../../../../../components/table/TableFunction";
import AttendanceDayContainer from "../../../Dining/Manage/SubComponents/AttendanceDayContainer";
import {VIEW_TYPE} from "../../../../../enums/ViewType";
import {DEFAULT_LEFT, DEFAULT_PAGINATION_START, SEARCH_MODEL} from "../../../../../components/table/Model/TableModel";
import {ATTENDANCE_MODES} from "../../../Dining/Manage/Models/Model";
import {getFullName} from "../../../../../Apis/functions";
import {ATTENDANCE_COLOR_MODEL} from '../Model/Model';

/**
 * @Email azapata@ibl.mx
 * @Author Ferro Zapata Arnulfo
 * @jobTitle Full Stack Developer
 * @createdAt 2025-10-07
 */

/**
 * Custom hook to manage attendance period table state and rendering.
 *
 * Handles:
 * - Attendance data fetching and filtering by date range
 * - Table head and body generation
 * - Pagination and search
 * - Rendering attendance day containers dynamically
 *
 * @param {Object} props - Hook/component parameters
 * @param {string} props.selectedOption - Current attendance mode (e.g., daily, weekly)
 * @param {string} props.searchElement - Search keyword
 * @param {Object} props.option - Additional options for search or table rendering
 * @param {Function} props.setRender - Function to set dynamic content rendering (e.g., AttendanceDayContainer)
 * @param {string} props.color - Theme color for table highlights
 * @param {Function} props.setBodyForExport - Function to prepare table data for export
 *
 *
 */


export const AttendancePeriodTableApp = ({
                                             selectedOption,
                                             searchElement,
                                             option,
                                             setRender,
                                             color,
                                             setBodyForExport,
                                             modified,
                                             setModified,
                                             prevIsPeriod
                                         }) => {

    const dispatch = useDispatch();
    const {t} = useTranslation();
    const [selectedDate, setSelectedDate] = useState(new Date());
    const { getAttendanceDate } = GetDateFilter();
    const [results, setResults] = useState({ content: [], totalElements: 0 });
    const [attendanceStartDate, setAttendanceStartDate] = useState(getAttendanceDate(selectedOption).start);
    const [attendanceEndDate, setAttendanceEndDate] = useState( getAttendanceDate(selectedOption).end);
    const [tableId] = useState('tbl_aw_attendance_daily');
    const [searchId] = useState('txt_aw_search_attendance_daily');
    const headRef = useRef();
    const leftPosition = DEFAULT_LEFT;
    const firstTime = useRef(true);

    const getThead = () => {
        return [
            <CustomTh tableId={tableId} fixedXY={true} left={leftPosition} classes="first_head w-190px">{t("name")}</CustomTh>,
            <CustomTh id="td_dat_second_column" tableId={tableId} headRef={headRef}/>,
        ]
    }

    const getTbody = () => {
        if (!results?.content?.length){
            setRender('');
            return  { body: [], data:[] };
        }

        const body = results?.content.map((member, i) => {
            if(i === 0){
                setRender(
                    <AttendanceDayContainer
                    color={color}
                    currentDays={member.attendances}
                    results={results?.content}
                    tableId={tableId}
                    headRef={headRef}
                    model={ATTENDANCE_COLOR_MODEL}
                />);
            }

            return [
                <CustomTd tableId={tableId} fixed={true} left={leftPosition} tooltip={true}  classes="h-90px first-column w-190px border-shades-200">
                    {member?.fullName || `${getFullName(member?.userName, member?.userLastName, member?.userSecondLastName)}`}
                </CustomTd>,
                <CustomTd tableId={tableId} classes={"h-90px"}/>,

            ]
        })

        return {
            body,
            data:results
        }
    }

    /**
     * Executes a paginated search request using basic criteria.
     *
     * Builds the request body with the current page, page size, selected model,
     * and any additional criteria passed as arguments.
     * Triggers the search action through Redux and updates the results on success.
     */
    const getData = (page, {type}) => {
        const aux = type ?? selectedOption;

        //Change from daily to period - NO EXECUTE with simple search
        if(searchElement !== "" && firstTime.current){
            firstTime.current = false;

            return;
        }

        setRender('');
        let { start, end } = getAttendanceDate(aux);
        setAttendanceStartDate(start);
        setAttendanceEndDate(end);
        const timezone = getTimezoneOffset();

        const body = SEARCH_MODEL.setObject({
            page,
            size: rowsPerPage,
            attendanceTimeZone: timezone,
            attendanceStartDate: start,
            attendanceEndDate: end,
            attendanceSearchType: aux,
            model: option,
            key: searchElement
        });
        setBodyForExport(body);
        dispatch(SimpleSearch({ body })).then(({ payload: { data: { data, success } } }) => {
            if (success) {
                setResults(data);
            }
        });
    };


    const {thead, tbody, pagination, page, setPage, rowsPerPage } = TableFunction({
        data: results,
        getThead,
        getTbody,
        view: VIEW_TYPE.LIST.name,
        labelRowsPerPage: 'attendance_per_page',
        fixedPagination: false,
        hasContainer: true,
        searchElement, //New
        getData,//New
        modified,//New
        setModified//New
    });

    const handleChange = (e) => {
        const type = e.target.value;

        //Change from period to daily, setting prev period to true
        if(type === ATTENDANCE_MODES.daily){
            prevIsPeriod.current = true;
            return;
        }
        getData(DEFAULT_PAGINATION_START, { type });
        setPage(DEFAULT_PAGINATION_START);
    }

    return {
        attendanceStartDate,
        attendanceEndDate,
        page,
        rowsPerPage,
        tableId,
        thead,
        tbody,
        color,
        t,
        pagination,
        searchId,
        setResults,
        selectedDate,
        setSelectedDate,
        handleChange
    }
}
